import { Layers, ScanSearch, FileText } from "lucide-react";
import { Sub, RefLink, P, Pill, ConnectsTo } from "./GuideHelpers";
import { PageCard } from "./PageCard";

export const SectionProductTabsLandscapesScorecard = () => (
  <>
    <div id="keyword-landscapes" className="scroll-mt-6">
      <PageCard icon={Layers} title="Landscapes" route="Tab 3" purpose="Competitive keyword ranking snapshots — your product vs. competitors across keyword groups.">
        <P>
          A Landscape is a <strong>point-in-time competitive analysis</strong>. You select a group of keywords,
          choose competitor ASINs, and Signal fetches the organic rank for each product across all keywords.
          The result is a color-coded grid showing who ranks where.
        </P>
        <P>
          Landscapes are organized under their respective <strong>priority search phrases</strong>.
          Previous landscape versions are nested within the search phrase row — click the row to expand
          and view historical versions.
        </P>
        <Sub>Landscape actions</Sub>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li><strong className="text-foreground">Refresh</strong> — Generates a new version of the landscape with current data.</li>
          <li><strong className="text-foreground">Schedule</strong> — Generates a new version on a one-time future date or on a recurring basis.</li>
          <li><strong className="text-foreground">Delete</strong> — Removes the landscape.</li>
          <li><strong className="text-foreground">Compare versions</strong> — Compare instances over time to see rank movement.</li>
        </ul>
        <Sub>Data source & related documentation</Sub>
        <P>
          Landscape data is sourced from <strong>Jungle Scout via Snowflake</strong> data share.
          Product enrichment and reverse keyword lookup queries each complete
          in approximately 1 minute 20 seconds on a Large warehouse.
        </P>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-2">
          <span>See also:</span>
          <RefLink to="ds-keyword-enrichment">§2 Keyword Enrichment</RefLink>
          <RefLink to="ds-landscape-data">§3 Landscape Data</RefLink>
          <RefLink to="ds-landscape-phases">§3a Generation Phases</RefLink>
          <RefLink to="ds-landscape-metrics">§3b Relevance &amp; KPS</RefLink>
        </div>
        <ConnectsTo pages={["Keywords", "Rank Tracker", "Dashboard", "Strategy", "Search Terms"]} />
      </PageCard>
    </div>

    <div id="content-scorecard-tab" className="scroll-mt-6">
      <PageCard icon={ScanSearch} title="Content Scorecard" route="Tab 4" purpose="AI-powered COSMO-aligned audit of the product's Amazon listing, embedded directly in Product Detail.">
        <P>
          The Content Scorecard evaluates how well this product's Amazon listing — title, bullets,
          description, A+ content, image text, and backend fields — answers the questions real
          shoppers ask. It uses Amazon's <strong>COSMO framework</strong>, a 15-question rubric
          spanning core usage, product identity, context, audience, and compatibility.
        </P>
        <P>
          <strong>No score is shown by default.</strong> Open the tab and click <strong>Run
          Scorecard</strong> — Signal assembles the ASIN, title, and brand, sends them to the
          Lovable AI Gateway (Gemini 3 Flash), and returns per-question scores, evidence-backed
          findings, recommended fixes, and a strategic summary for listings, A+ content, and the
          Brand Store.
        </P>
        <Sub>What you get back</Sub>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li><strong className="text-foreground">Overall grade</strong> — a 0–100 score and letter grade for the listing.</li>
          <li><strong className="text-foreground">Category breakdown</strong> — scores for each COSMO dimension (Core Usage, Product Identity, Context, Audience, Compatibility).</li>
          <li><strong className="text-foreground">Per-question findings</strong> — for each of the 15 COSMO questions: a score, an explanation, an evidence snippet pulled from the listing, and a specific recommendation.</li>
          <li><strong className="text-foreground">Strategic summary</strong> — three paragraphs covering listing copy, A+ content, and Brand Store priorities.</li>
        </ul>
        <Sub>Scoring model</Sub>
        <P>
          Each question is scored as: <strong>(explicitness × 0.40) + (evidence_strength × 0.30) +
          (coverage_quality × 0.20) + (field_diversity × 0.10)</strong>. The overall grade is a
          weighted average across dimensions.
        </P>
        <Sub>Why it lives here</Sub>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Quick access — audit the product you're already working on without navigating away.</li>
          <li>Side-by-side workflow — review the scorecard, then switch tabs to apply keyword or content changes.</li>
          <li>Periodic re-audits — re-run after listing changes to measure improvement.</li>
        </ul>
        <P>
          For the full scoring model, COSMO question list, dimension weights, and edge-function
          implementation, see <RefLink to="ds-content-scorecard">Data Sources §7 — Content Scorecard</RefLink>.
          To audit an ASIN that isn't in your brand, use the <RefLink to="spi">standalone Content Scorecard</RefLink>.
        </P>
        <ConnectsTo pages={["Strategy", "Keywords", "A/B Tests", "Content Scorecard (standalone)"]} />
      </PageCard>
    </div>

    <div id="search-terms" className="scroll-mt-6">
      <PageCard icon={FileText} title="Search Terms" route="Tab 5" purpose="Backend search term optimization for Amazon's hidden keyword fields.">
        <P>
          Amazon allows sellers to input backend search terms that aren't visible to shoppers but influence
          search ranking. This tab helps you <strong>generate optimized search term sets</strong> from your
          Keyword Bank, ensuring you maximize the character limit without wasting space.
        </P>
        <P>
          <strong>Prerequisite:</strong> Search terms can only be generated when at least one landscape
          is available for the product. If no landscapes exist, you'll be prompted to generate one first.
        </P>
        <Sub>Generation process</Sub>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Uses the highest-KPS keywords from all associated keyword landscapes.</li>
          <li>Strips keywords already present in the title, brand, or bullet points.</li>
          <li>Removes competitor brands and restricted/prohibited terms.</li>
          <li>Eliminates conjunctions and prepositions.</li>
          <li>Deduplicates the final results.</li>
          <li>Prioritizes unique, high-value individual keywords for maximum backend effectiveness.</li>
        </ul>
        <Sub>Prohibited keywords integration</Sub>
        <P>
          Generation automatically excludes any term that appears in the global, brand, or
          product-level prohibited keyword lists — you never have to scrub the output by hand.
          See the global <RefLink to="prohibited-keywords">Prohibited Keywords</RefLink> page
          and the brand-level list on <RefLink to="brand-context">Brand Context</RefLink>.
        </P>
        <P>
          After a set is generated, you can also block terms inline:{" "}
          <strong className="text-foreground">highlight any word</strong> in a generated search
          term set and a small <Pill>Add to prohibited keywords</Pill> menu pops up above the
          selection. Clicking it opens the standard add-keyword flow, where you choose whether
          the term should be blocked globally, for this brand, or for this product only — the
          same scopes available everywhere else in Signal. Once added, future generations on
          this product (and anywhere the chosen scope applies) will automatically exclude it.
        </P>
        <ConnectsTo pages={["Keywords", "Landscapes", "Strategy", "Prohibited Keywords", "Brand Context"]} />
      </PageCard>
    </div>
  </>
);
